/**
 * ReferencesModule — in-app knowledge base / glossary.
 *
 * Renders a searchable, categorised list of platform concepts, frameworks,
 * and definitions. All content is static (REFERENCES_DATA) — no API calls.
 * Categories are colour-coded via CATEGORY_META.
 */
import { useState } from "react"
import {
  Search, Target, Layers, TrendingUp, Users2, ChevronDown,
  Award, Briefcase, GraduationCap, LineChart, CheckCircle, Settings
} from "lucide-react"
import "./references-module.css"

const CATEGORY_META = {
  "Platform Overview": { color: "#006B54", bg: "#ecfdf5", border: "#a7f3d0" },
  "Key Concepts":      { color: "#2d4a9e", bg: "#eef2ff", border: "#bfcbf7" },
  "Frameworks":        { color: "#92650a", bg: "#fefce8", border: "#fde68a" },
  "Strategy Areas":    { color: "#0d7377", bg: "#ecfeff", border: "#a5f3fc" },
  "Measurement and Evaluation": { color: "#7B0808", bg: "#fff5f5", border: "#fecaca" },
}

const REFERENCES_DATA = [
  {
    id: 1,
    category: "Platform Overview",
    title: "What is TalentScope360",
    icon: Layers,
    description: "A web-based talent management solution supporting competency-based management decisions that impact People and Culture.",
    details: "TalentScope360 is a comprehensive solution that supports your end-to-end talent management initiatives across several critical areas including talent gap analysis, manpower planning, recruitment and selection, career planning, succession, learning and development, performance management, and culture change and transformation. Built on years of experience in strategy and performance management across various organizations worldwide, TalentScope360 is ideal for organizations of all sizes committed to building strong capabilities for implementing organizational strategy at both individual and team levels.",
    keyAreas: [
      "Talent gap analysis and manpower planning",
      "Recruitment and selection",
      "Career planning and succession",
      "Learning and development",
      "Performance management",
      "Culture, change and transformation"
    ]
  },
  {
    id: 2,
    category: "Key Concepts",
    title: "Competence",
    icon: Target,
    description: "The overall capability that one demonstrates in their role, representing an acceptable level of performance.",
    details: "Competence refers to the overall capability that one demonstrates in their role. It is clearly visible when it is present, and glaringly absent when it is not. To be considered competent in a given role, one must consistently demonstrate proficiency in competencies that are considered relevant to the role and that make a substantive contribution to effective performance, both within and beyond the organization.",
  },
  {
    id: 3,
    category: "Key Concepts",
    title: "Competencies",
    icon: TrendingUp,
    description: "The knowledge, skills and behaviors that individuals need to perform effectively in a given job role.",
    details: "Competencies refer to the knowledge, skills and behaviors that individuals are required to demonstrate in order to be considered competent at what they do at work. The prescribed competencies in a particular role are the minimum performance standards needed to perform effectively in that role. The relevance of a competency is critical to consider, since different organizations will place different levels of importance to different competencies at different times.",
  },
  {
    id: 4,
    category: "Key Concepts",
    title: "Competency Categories",
    icon: Users2,
    description: "Top-level scope of performance areas that organizations use to structure their competency frameworks.",
    details: "Competency categories prescribe an organization's top-level scope of performance areas, and different organizations structure this scope differently. Most organizations will have a category of competencies called Core competencies, which outline the breadth and depth of specific technical and behavioral orientations that all employees are required to demonstrate. A second common category is Technical or Functional Competencies, which apply to specific job-families within the organization.",
  },
  {
    id: 5,
    category: "Key Concepts",
    title: "Competency Domains",
    icon: Briefcase,
    description: "Core areas of performance capability within the competency model that define specific capability clusters.",
    details: "Competency domains refer to a core area of performance capability within the competency model. While competency categories refer to an organization's top-level scope of performance areas, competency domains refer to specific capability clusters within those categories. Competence models will typically have between four and six competency domains, under which come the respective competencies in each domain. The choice of which competency domains to adopt depends on an organization's mission, vision, values and strategy at a particular time.",
  },
  {
    id: 6,
    category: "Frameworks",
    title: "Competence Framework",
    icon: Settings,
    description: "The structural relationships that make up an organization's overall competence strategy.",
    details: "A competence framework refers to the structural relationships that make up an organization's overall competence strategy. It provides the broad framework within which more detailed performance requirements are considered, without reference to specific contexts. The framework answers critical questions about competency categories, proficiency levels, measurement metrics, evaluation methods, and integration with other talent management areas.",
  },
  {
    id: 7,
    category: "Frameworks",
    title: "Linear Competency Modeling",
    icon: LineChart,
    description: "A competency model that presumes uni-directional linear progression from one level to the next.",
    details: "A linear competency model presumes uni-directional linear progression of competencies from one level to the next, with this progression tied to similarly progressing job roles. It may classify competencies into progressive levels from Basic to Intermediate, to Advanced and to Innovator. Linear competency modeling presumes incremental growth in breadth, depth and complexity of competencies with each level, with each competency building on the previous one in a sequentially progressive manner. It is relatively easy to design and simple to administer, particularly in environments where roles evolve in predictable sequence.",
  },
  {
    id: 8,
    category: "Frameworks",
    title: "Non-Linear Competency Modeling",
    icon: Layers,
    description: "Disaggregated competency modeling that breaks competencies into clusters for independent advancement.",
    details: "Non-linear or disaggregated competency modeling breaks competencies into clusters but still within an indicated growth in breadth, depth and complexity of competencies within a particular competency domain. This provides for the independent advancement of each cluster's competencies without a presumed relationship between the clusters. It makes it possible for employees to excel in certain technical areas without taking on management responsibilities, and enables highly agile talent management for dynamic work environments.",
    objectives: [
      "Provides for excellence at individual job levels without implied need to advance",
      "Supports employees who want to excel in technical areas without management roles",
      "Identifies potential underutilization of capacity or career mismatches",
      "Enables highly agile talent management",
      "Effectively supports personalized development",
      "Adaptable for project-based and dynamic work environments"
    ]
  },
  {
    id: 9,
    category: "Strategy Areas",
    title: "Competency Structure",
    icon: Target,
    description: "The starting point for talent management strategy, determining your organization's competency categories.",
    details: "This is the starting point in developing your talent management strategy. It is the point at which you need to determine your organization's competency categories. Different organizations will have different competency categories, with most settling for two: core competencies and technical or functional competencies. Others have more. The choice of competency categories depends on the organization's size, structure, industry, culture, strategy and even external factors such as market and technology.",
  },
  {
    id: 10,
    category: "Strategy Areas",
    title: "Enterprise-Wide Talent Map",
    icon: Users2,
    description: "Your global enterprise-wide talent map based on core competencies and overall performance requirements.",
    details: "Your global enterprise-wide talent map is based on what has sometimes been called your core competencies. The question here is what performance expectations will you want to set against your core competencies across the organization. This establishes your organization's key performance drivers and the contribution that each employee is expected to make in clearly defined areas.",
  },
  {
    id: 11,
    category: "Strategy Areas",
    title: "Individual Talent Planning",
    icon: GraduationCap,
    description: "Personal talent objectives matched to role performance requirements and development areas.",
    details: "The value of competency-based management lies in how individual performance is shaped by the role's competency model. Individual talent planning offers each employee the opportunity to set personal talent objectives matched to their role's performance requirements, and points to areas of development they may want to pay attention to. Different organizations have different approaches to individual talent planning, with some favoring a top-down approach, others a bottom-up approach, and others a 360 degrees approach.",
  },
  {
    id: 12,
    category: "Measurement and Evaluation",
    title: "Measurement vs Evaluation",
    icon: Award,
    description: "Understanding the difference between measuring competence and evaluating performance.",
    details: "TalentScope360 is not a performance management system; it is a talent management system. Talent is what drives performance. Measurement is the quantification of given attributes—examining an employee's competence on a scale of 1 to 5. Evaluation is the attaching of a judgement to the measurement against a set standard—determining what a score of 3 out of 5 means and whether it is good or bad.",
  },
  {
    id: 13,
    category: "Measurement and Evaluation",
    title: "Validity of Measurements",
    icon: CheckCircle,
    description: "How effectively the competency model measures what it is supposed to measure.",
    details: "Validity in measurement examines how effectively the competency model measures what it is supposed to measure. There are three types of validity criteria: Content Validity (whether measurement tools cover all relevant aspects), Construct Validity (whether tools align with theoretical concepts), and Criterion-related Validity (whether measurements correlate with independently observable reality).",
    objectives: [
      "Content Validity: Comprehensive coverage of all relevant competence aspects",
      "Construct Validity: Alignment with theoretical concepts underlying competencies",
      "Criterion-related Validity: Correlation with independently observable reality"
    ]
  },
  {
    id: 14,
    category: "Measurement and Evaluation",
    title: "Reliability of Measurements",
    icon: TrendingUp,
    description: "The consistency of measurements over time and across different conditions.",
    details: "Reliability of measurements focuses on the consistency of measurements over time and across different conditions. It examines whether measuring the same competencies in the same people repeatedly under different conditions would yield the same results. A reliable competency model would establish a strong correlation between a given level of competence and actual performance across many employees, irrespective of the number of raters.",
    objectives: [
      "Test-retest reliability: Consistency of scores over time by the same rater",
      "Inter-rater reliability: Consistency of scores across multiple similar assessors",
      "Internal consistency: Alignment of competencies with performance expectations at each level"
    ]
  }
]

const CATEGORIES = ["All", "Platform Overview", "Key Concepts", "Frameworks", "Strategy Areas", "Measurement and Evaluation"]

function ReferenceCard({ reference, isExpanded, onToggle }) {
  const Icon = reference.icon
  const meta = CATEGORY_META[reference.category] || CATEGORY_META["Platform Overview"]

  return (
    <div
      className={`ref-card ${isExpanded ? "ref-card--open" : ""}`}
      style={{ "--card-color": meta.color, "--card-bg": meta.bg, "--card-border": meta.border }}
    >
      <button className="ref-card-header" onClick={onToggle}>
        <div className="ref-card-left-bar" />
        <div className="ref-card-icon-wrap">
          <Icon size={18} />
        </div>
        <div className="ref-card-body">
          <span className="ref-category-tag">{reference.category}</span>
          <h3 className="ref-title">{reference.title}</h3>
          <p className="ref-description">{reference.description}</p>
        </div>
        <ChevronDown className={`ref-chevron ${isExpanded ? "ref-chevron--open" : ""}`} size={18} />
      </button>

      {isExpanded && (
        <div className="ref-expanded-body">
          <p className="ref-details">{reference.details}</p>

          {reference.keyAreas && (
            <div className="ref-list-block">
              <h4 className="ref-list-heading">Key Areas</h4>
              <ul className="ref-bullet-list">
                {reference.keyAreas.map((area, i) => (
                  <li key={i}>{area}</li>
                ))}
              </ul>
            </div>
          )}

          {reference.objectives && (
            <div className="ref-list-block">
              <h4 className="ref-list-heading">Key Characteristics</h4>
              <ul className="ref-bullet-list">
                {reference.objectives.map((obj, i) => (
                  <li key={i}>{obj}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ReferencesModule() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [expandedId, setExpandedId] = useState(null)

  const filteredReferences = REFERENCES_DATA.filter((ref) => {
    const q = searchTerm.toLowerCase()
    const matchesSearch =
      ref.title.toLowerCase().includes(q) ||
      ref.description.toLowerCase().includes(q) ||
      ref.details.toLowerCase().includes(q)
    const matchesCategory = selectedCategory === "All" || ref.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <main className="ref-page">
      {/* Hero / Search */}
      <div className="ref-hero">
        <div className="ref-hero-inner">
          <h1 className="ref-hero-title">References</h1>
          <p className="ref-hero-subtitle">
            Explore key concepts, frameworks, and competency modeling approaches that power TalentScope360.
          </p>
          <div className="ref-search-box">
            <Search className="ref-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search references..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ref-search-input"
            />
            {searchTerm && (
              <button className="ref-search-clear" onClick={() => setSearchTerm("")}>
                ×
              </button>
            )}
          </div>
        </div>
        <div className="ref-hero-orb ref-hero-orb--1" />
        <div className="ref-hero-orb ref-hero-orb--2" />
      </div>

      {/* Content */}
      <div className="ref-content">
        {/* Category filters */}
        <div className="ref-filter-bar">
          {CATEGORIES.map((cat) => {
            const meta = CATEGORY_META[cat]
            const isActive = selectedCategory === cat
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`ref-filter-btn ${isActive ? "ref-filter-btn--active" : ""}`}
                style={
                  isActive && meta
                    ? { backgroundColor: meta.color, borderColor: meta.color, color: "#fff" }
                    : meta
                    ? { "--hover-color": meta.color }
                    : {}
                }
              >
                {cat}
              </button>
            )
          })}
        </div>

        {/* Count */}
        <div className="ref-count">
          {filteredReferences.length} of {REFERENCES_DATA.length} references
        </div>

        {/* Cards */}
        <div className="ref-list">
          {filteredReferences.length > 0 ? (
            filteredReferences.map((ref) => (
              <ReferenceCard
                key={ref.id}
                reference={ref}
                isExpanded={expandedId === ref.id}
                onToggle={() => setExpandedId(expandedId === ref.id ? null : ref.id)}
              />
            ))
          ) : (
            <div className="ref-empty">
              <Search size={40} className="ref-empty-icon" />
              <p className="ref-empty-title">No results found</p>
              <p className="ref-empty-sub">Try adjusting your search or selecting a different category.</p>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
